import { useRef, type CSSProperties } from "react";
import { Check, Heart, Sparkles, ChartNoAxesColumnIncreasing } from "lucide-react";
import { TRIGGERS } from "@/components/landing/landing-data";
import { QuestionsVisual } from "@/components/landing/questions-visual";
import { QuestionsCopy } from "@/components/landing/questions-copy";
import {
  useEnteredView,
  useLandingReducedMotion,
  useOnScreen,
  usePageVisible,
} from "@/components/landing/landing-motion";

const TRIGGER_ICONS = [Sparkles, Heart, ChartNoAxesColumnIncreasing];

/** "Your Unfold triggers are ready." Used by the "From reflection to action" demo. */
export function ReadyBadge({ className = "", style }: { className?: string; style?: CSSProperties }) {
  return (
    <p className={`flex items-center gap-3 text-[15px] text-(--lp-body) sm:text-[17px] ${className}`} style={style}>
      <span className="lp-ok" aria-hidden>
        <Check className="h-3.5 w-3.5" strokeWidth={3} />
      </span>
      Your Unfold triggers are ready.
    </p>
  );
}

/** A trigger's icon and two-line label (used by the demo). Each line stays whole, so a label never ends on a stranded word. */
export function TriggerChip({
  index,
  className = "",
  textClass = "text-[15px] sm:text-[17px]",
}: {
  index: number;
  className?: string;
  textClass?: string;
}) {
  const t = TRIGGERS[index];
  const Icon = TRIGGER_ICONS[index];
  return (
    <span className={`flex items-center gap-4 ${className}`}>
      <span className={`lp-tic lp-tone-${t.tone}`} aria-hidden>
        <Icon className="h-6 w-6" strokeWidth={1.7} />
      </span>
      <span className={textClass}>
        <span className="block whitespace-nowrap">{t.line1}</span>
        <span className="block whitespace-nowrap">{t.line2}</span>
      </span>
    </span>
  );
}

/**
 * "UnfoldWin turns reflection into momentum." The animated visual (QuestionsVisual) sits
 * beside the animated copy (QuestionsCopy): eyebrow, heading and paragraph.
 */
export function QuestionsSection() {
  const sectionRef = useRef<HTMLElement>(null);
  const reduce = useLandingReducedMotion();
  const onScreen = useOnScreen(sectionRef, 0.05);
  const pageVisible = usePageVisible();
  const visualRef = useRef<HTMLDivElement>(null);
  const visualEntered = useEnteredView(visualRef, 0.35);

  return (
    <section
      ref={sectionRef}
      id="features"
      aria-labelledby="lp-questions-title"
      data-paused={!onScreen || !pageVisible}
      className="relative py-20 md:py-[110px] md:pb-[120px]"
    >
      <div className="mx-auto grid max-w-[1200px] grid-cols-1 items-center gap-14 px-4 sm:px-8 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.15fr)] lg:gap-[70px] lg:px-12">
        {/* Visual */}
        <div ref={visualRef} className="relative order-2 mx-auto w-full max-w-[440px] lg:order-1 lg:max-w-none" aria-hidden>
          <QuestionsVisual sectionRef={sectionRef} active={onScreen && pageVisible} entered={visualEntered} reduce={reduce} />
        </div>

        {/* Copy */}
        <div className="order-1 lg:order-2">
          <QuestionsCopy reduce={reduce} active={onScreen && pageVisible} />
        </div>
      </div>
    </section>
  );
}
